import React from 'react';
import Icon from '../../../components/AppIcon';

const TenantCommunicationLog = ({ notifications, suspensionHistory }) => {
  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (type) => {
    const icons = {
      system: 'Bell',
      payment: 'DollarSign',
      suspension: 'Ban',
      alert: 'AlertTriangle'
    };
    return icons?.[type] || 'MessageCircle';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'text-destructive',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-muted-foreground'
    };
    return colors?.[priority] || 'text-foreground';
  };

  // Combine notifications and suspension events
  const communicationLog = [
    ...notifications?.map(notif => ({
      type: 'notification',
      date: notif?.created_at,
      title: notif?.title,
      message: notif?.message,
      priority: notif?.priority,
      id: notif?.id
    })),
    ...suspensionHistory?.map(suspension => ({
      type: 'suspension_event',
      date: suspension?.suspended_at,
      title: suspension?.is_active ? 'Tenant Suspended' : 'Tenant Reactivated',
      message: suspension?.is_active
        ? suspension?.suspension_notes
        : suspension?.reactivation_notes,
      priority: 'high',
      id: suspension?.id
    }))
  ]?.sort((a, b) => new Date(b?.date) - new Date(a?.date));

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
        Communication & Activity Log
      </h2>

      {communicationLog?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="MessageCircle" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No communication history available</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {communicationLog?.map((log) => (
            <div key={log?.id} className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  log?.priority === 'critical' ? 'bg-destructive/10' :
                  log?.priority === 'high'? 'bg-warning/10' : 'bg-primary/10'
                }`}>
                  <Icon
                    name={getNotificationIcon(log?.type)}
                    size={16}
                    className={getPriorityColor(log?.priority)}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-semibold text-foreground">{log?.title}</h4>
                    <span className="text-xs text-muted-foreground">{formatDate(log?.date)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{log?.message}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      log?.priority === 'critical' ? 'bg-destructive/10 text-destructive' :
                      log?.priority === 'high' ? 'bg-warning/10 text-warning' :
                      log?.priority === 'medium'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>
                      {log?.priority}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {log?.type?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TenantCommunicationLog;